import {
	MenuItem,
	MenuItemProps,
	MenuList,
	MenuProps,
	PaperProps,
	Popover,
	PopoverActions,
	PopoverOrigin,
	PopoverProps,
} from "@mui/material";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";
import styles from "./index.module.css";

export type CoreMenuPopoverProps = Omit<PopoverProps, "ref">;

export type CoreMenuProps = CoreMenuPopoverProps & {
	slotProps?: CoreMenuPopoverProps["slotProps"] & {
		menuList?: Omit<Exclude<MenuProps["MenuListProps"], undefined>, "aria-labelledby">;
	};
};

export type CoreMenuItemProps = Omit<MenuItemProps, "value"> & {
	// MenuItem's default root is 'li', so MUI types `value` as LiHTMLAttributes["value"]
	// (string | number | readonly string[]), even though Select reads it as an arbitrary value at runtime.
	value?: unknown;
} & Pick<FormControlTooltipProps<false>, "tooltip" | "disabledTooltip" | "slotProps">;

const ARROW_RADIUS = 8;
const ARROW_OFFSET_VERTICAL = 5;
const ARROW_OFFSET_HORIZONTAL = 15;

/**
 * Distance in px from an element's left edge to the point a `PopoverOrigin`'s horizontal value names —
 * the same mapping MUI's own Popover positioning uses, so the arrow below can be placed from the origins
 * alone instead of from a second measurement of where the paper ended up.
 */
const getHorizontalOriginOffset = (width: number, origin: PopoverOrigin["horizontal"]): number => {
	if (origin === "left") return 0;
	if (origin === "center") return width / 2;
	if (origin === "right") return width;
	return origin;
};

export function CoreMenuPopover({
	anchorOrigin: specifiedAnchorOrigin,
	transformOrigin: specifiedTransformOrigin,
	slotProps,
	children,
	...popoverProps
}: CoreMenuPopoverProps) {
	const { anchorEl, open, onClose, container } = popoverProps;
	const anchorElement = typeof anchorEl === "function" ? anchorEl() : anchorEl;

	const paperRef = useRef<HTMLDivElement | null>(null);
	const popoverActionsRef = useRef<PopoverActions | null>(null);

	// Measured once, as the paper mounts, and deliberately not re-measured while it stays open. These are
	// layout values, so — unlike the getBoundingClientRect() a ResizeObserver used to poll here — the open
	// transition scaling the paper doesn't skew them and there's nothing to keep polling for. Holding them
	// still is the point: the side the menu opens on is decided from the height, so re-deciding it live
	// flipped an already-open menu over to the other side of its button the moment its content grew by a
	// line — which is what a menu item relabelling itself mid-action does ("Generate Report" ticking over
	// to "Generating..." and back, with "Regenerate Report" appearing underneath).
	const [paperSize, setPaperSize] = useState<{ width: number; height: number } | undefined>();
	const popoverElementCallback = useCallback((elem: HTMLDivElement | null) => {
		paperRef.current = elem;
		setPaperSize(elem ? { width: elem.offsetWidth, height: elem.offsetHeight } : undefined);
	}, []);

	// The popover is rendered non-modally so that the page behind it keeps scrolling, which means it
	// no longer follows its anchor and no backdrop is left to close it.
	useEffect(() => {
		if (!open) return undefined;

		// Read from the container rather than from the paper: MUI's Portal only attaches in a layout
		// effect, so the paper doesn't exist yet on the render that opens the popover - but the container
		// is where it is about to be put, and that is what decides which roots the listeners need.
		const containerElement = typeof container === "function" ? container() : container;
		const containerRoot = containerElement?.getRootNode();
		const paperRoot = containerRoot instanceof ShadowRoot ? containerRoot : undefined;

		const reposition = () => popoverActionsRef.current?.updatePosition();
		const closeOnOutsideClick = (event: Event) => {
			// composedPath, because these components are also rendered inside a shadow root
			const path = event.composedPath();
			if (paperRef.current && path.includes(paperRef.current)) return;

			// composedPath only reveals a shadow tree to listeners inside it, so a *closed* root - the
			// extension's toolbar - hands `document` a path that stops at the host, making a click on the
			// paper indistinguishable from a click beside it. Deferring those to the listener on the root
			// itself, where the path is whole, is what stops a menu item closing the menu before its own
			// onClick has run.
			if (paperRoot && event.currentTarget !== paperRoot && path.includes(paperRoot.host)) return;

			if (anchorElement && path.some((node) => node === anchorElement)) {
				// Swallow the click, like the removed backdrop did, so the anchor cannot reopen the menu
				event.preventDefault();
				event.stopPropagation();
			}
			onClose?.(event, "backdropClick");
		};

		window.addEventListener("scroll", reposition, true);
		// Capturing, because handlers on the page stop clicks from reaching the document
		document.addEventListener("click", closeOnOutsideClick, true);
		paperRoot?.addEventListener("click", closeOnOutsideClick, true);
		return () => {
			window.removeEventListener("scroll", reposition, true);
			document.removeEventListener("click", closeOnOutsideClick, true);
			paperRoot?.removeEventListener("click", closeOnOutsideClick, true);
		};
	}, [open, anchorElement, onClose, container]);

	const [anchorOrigin, transformOrigin] = useMemo((): [PopoverOrigin, PopoverOrigin] => {
		let autoFlip = false;
		if (!specifiedAnchorOrigin && !specifiedTransformOrigin && anchorElement && paperSize && window.visualViewport) {
			const anchorGlobalPosition = anchorElement.getBoundingClientRect();
			const menuHeight = paperSize.height + ARROW_OFFSET_VERTICAL + ARROW_RADIUS;
			const anchorTopSpace = anchorGlobalPosition.top - window.visualViewport.offsetTop;
			const anchorBottomSpace =
				window.visualViewport.offsetTop + window.visualViewport.height - anchorGlobalPosition.bottom;
			autoFlip = anchorBottomSpace <= menuHeight && anchorTopSpace > anchorBottomSpace;
		}
		return [
			specifiedAnchorOrigin ?? {
				vertical: autoFlip ? "top" : "bottom",
				horizontal: "center",
			},
			specifiedTransformOrigin ?? {
				vertical: autoFlip ? "bottom" : "top",
				horizontal: "right",
			},
		];
	}, [paperSize, anchorElement, specifiedAnchorOrigin, specifiedTransformOrigin]);

	let verticalTransform = 0;
	if (transformOrigin.vertical === "top" || transformOrigin.vertical === "bottom") {
		verticalTransform = ARROW_OFFSET_VERTICAL + ARROW_RADIUS;
		if (transformOrigin.vertical === "bottom") {
			verticalTransform *= -1;
		}
	}

	let horizontalTransform = 0;
	if (transformOrigin.horizontal === "left" || transformOrigin.horizontal === "right") {
		horizontalTransform = ARROW_OFFSET_HORIZONTAL + ARROW_RADIUS * 2;
		if (transformOrigin.horizontal === "left") {
			horizontalTransform *= -1;
		}
	}

	// MUI lines the paper's `transformOrigin` point up with the anchor's `anchorOrigin` point and — with
	// marginThreshold null, so nothing nudges it back inside the viewport afterwards — leaves it there.
	// That fixes where the anchor's centre sits relative to the paper's own transform-origin edge whatever
	// the paper's width, so the arrow is placed as a percentage of that width plus a constant rather than
	// from a measurement of where the paper landed. A measured offset is a frame behind any content change,
	// which is what slid the arrow out from under its button (and back) each time a menu item's label grew
	// or shrank while the menu was open.
	const arrowLeft = useMemo(() => {
		if (!anchorElement || !paperSize) return undefined;

		const anchorWidth = anchorElement.getBoundingClientRect().width;
		const offsetFromOriginEdge =
			anchorWidth / 2 -
			getHorizontalOriginOffset(anchorWidth, anchorOrigin.horizontal) -
			horizontalTransform -
			ARROW_RADIUS;

		// Same bounds check the measured version did: an arrow that would hang off either end of the paper
		// isn't drawn at all.
		const offsetFromPaperLeft =
			getHorizontalOriginOffset(paperSize.width, transformOrigin.horizontal) + offsetFromOriginEdge;
		if (offsetFromPaperLeft < 0 || offsetFromPaperLeft > paperSize.width - ARROW_RADIUS * 2) {
			return undefined;
		}

		const originEdge =
			typeof transformOrigin.horizontal === "number"
				? `${transformOrigin.horizontal}px`
				: { left: "0%", center: "50%", right: "100%" }[transformOrigin.horizontal];
		return `calc(${originEdge} ${offsetFromOriginEdge < 0 ? "-" : "+"} ${Math.abs(offsetFromOriginEdge)}px)`;
	}, [anchorElement, paperSize, anchorOrigin.horizontal, transformOrigin.horizontal, horizontalTransform]);

	const generatePaperProps = (specifiedProps?: Partial<PaperProps>): Partial<PaperProps> => {
		return {
			className: styles.displayedPaper,
			sx: {
				...specifiedProps?.sx,
				"&::before": {
					visibility: arrowLeft == null ? "hidden" : "visible",
					left: arrowLeft,
					...(transformOrigin.vertical === "bottom"
						? {
								// Menu opening from top
								top: "calc(100% - 11px)",
								transform: "rotate(225deg)",
							}
						: {
								// Menu opening from bottom
								bottom: "calc(100% - 11px)",
								transform: "rotate(45deg)",
							}),
				},
			},
			style: {
				transform: `translate(${horizontalTransform}px, ${verticalTransform}px)`,
				pointerEvents: "auto",
			},
			ref: popoverElementCallback,
		};
	};

	const specifiedObjectPaperProps = typeof slotProps?.paper === "function" ? undefined : slotProps?.paper;

	return (
		<Popover
			{...popoverProps}
			action={popoverActionsRef}
			anchorOrigin={anchorOrigin}
			transformOrigin={transformOrigin}
			hideBackdrop
			disableScrollLock
			style={{ pointerEvents: "none", ...popoverProps.style }}
			// only deprecated PaperProps is working for some reason
			PaperProps={{
				...specifiedObjectPaperProps,
				...generatePaperProps(specifiedObjectPaperProps),
			}}
			marginThreshold={null}
		>
			{children}
		</Popover>
	);
}

export default function CoreMenu({
	"aria-labelledby": labelledBy,
	slotProps,
	children,
	...popoverProps
}: CoreMenuProps) {
	const [inShadowDom, setInShadowDom] = useState<boolean>(false);

	// Using activeIndex instead of querying at runtime because MUI does
	// weird things in Shadow dom and doesn't give correct element that is active
	const [activeIndex, setActiveIndex] = useState<number>(0);
	const menuListRef = useRef<HTMLUListElement | null>(null);

	const focusableMenuItemsSelector = "[role='menuitem']:not([disabled]):not([aria-disabled='true'])";

	const setMenuListRef = useCallback((node: HTMLUListElement | null) => {
		if (node) {
			menuListRef.current = node;
			setInShadowDom(node.getRootNode() instanceof ShadowRoot);
		}
	}, []);

	// On menu open, first menu item doesn't get focused, ONLY in shadow dom
	useEffect(() => {
		if (inShadowDom && menuListRef.current) {
			const items: HTMLElement[] = Array.from(
				menuListRef.current.querySelectorAll<HTMLElement>(focusableMenuItemsSelector) ?? [],
			);

			if (items.length > 0) {
				items[0].focus();
				setActiveIndex(0);
			}
		}
	}, [inShadowDom]);

	return (
		<CoreMenuPopover {...popoverProps}>
			<MenuList
				// autoFocusItem does not work in shadow dom
				autoFocusItem={!inShadowDom}
				{...slotProps?.menuList}
				className={`${styles.menuList} ${slotProps?.menuList?.className || ""}`}
				aria-labelledby={labelledBy}
				ref={setMenuListRef}
				onKeyDown={(event: React.KeyboardEvent<HTMLUListElement>) => {
					// Patch arrow keys not working in Shadow dom
					if ((event.key === "ArrowDown" || event.key === "ArrowUp") && inShadowDom) {
						event.preventDefault();
						event.stopPropagation();

						const items: HTMLElement[] = Array.from(
							menuListRef.current?.querySelectorAll<HTMLElement>(focusableMenuItemsSelector) ?? [],
						);

						if (!items.length) return;
						const goingDown = event.key === "ArrowDown";
						const nextIndex =
							activeIndex === -1
								? goingDown
									? 0
									: items.length - 1
								: goingDown
									? (activeIndex + 1) % items.length
									: (activeIndex - 1 + items.length) % items.length;

						setActiveIndex(nextIndex);
						items[nextIndex]?.focus();
					}
					// Mui doesn't support Tab closing the menu
					else if (event.key === "Escape" || event.key === "Tab") {
						event.preventDefault();
						const anchor = popoverProps.anchorEl as HTMLElement | null;
						popoverProps.onClose?.(event, "escapeKeyDown");

						// Focus doesn't return to menu button on close, ONLY in shadow dom
						if (anchor && inShadowDom) {
							// Delay to let Popover unmount cleanly
							setTimeout(() => anchor.focus(), 0);
						}
					}
				}}
			>
				{children}
			</MenuList>
		</CoreMenuPopover>
	);
}

export const CoreMenuItem = forwardRef<HTMLLIElement, CoreMenuItemProps>(function ArrowMenuItem(
	{ onClick, disabled, tooltip, disabledTooltip, className, children, slotProps, value, ...menuItemProps },
	ref,
) {
	return (
		<FormControlTooltip
			tooltip={tooltip}
			disabledTooltip={disabledTooltip}
			wrapDisabledChild={false}
			slotProps={{ ...slotProps, tooltip: { describeChild: true, ...slotProps?.tooltip } }}
		>
			<MenuItem
				{...menuItemProps}
				// MenuItem's `value` is typed as LiHTMLAttributes["value"], but Select reads it as an
				// arbitrary value at runtime, so CoreMenuItemProps widens it to `unknown` for consumers.
				value={value as MenuItemProps["value"]}
				disabled={disabled}
				onClick={disabled ? undefined : onClick}
				className={`${styles.menuItem} ${className || ""}`}
				ref={ref}
			>
				{children}
			</MenuItem>
		</FormControlTooltip>
	);
});
